import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../../components/AnimatedSection';
import { ChevronLeftIcon, ChevronRightIcon } from '../../components/Icons';
import { db } from '../../firebase'; 
import { 
  collection, query, orderBy, limit, getDocs, 
  startAfter, endBefore, getCountFromServer, limitToLast 
} from 'firebase/firestore';

const ProjectsPage = () => { 
  const [constructionPhotos, setConstructionPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9; 
  const [lastVisibleDoc, setLastVisibleDoc] = useState(null); 
  const [firstVisibleDoc, setFirstVisibleDoc] = useState(null); 
  const [isLastPage, setIsLastPage] = useState(false);
  const [totalImages, setTotalImages] = useState(0);
  const [pageDirection, setPageDirection] = useState('initial');

  useEffect(() => {
    const getTotalImages = async () => {
      try {
        const coll = collection(db, "constructionImages");
        const snapshot = await getCountFromServer(coll);
        setTotalImages(snapshot.data().count);
      } catch (error) {
        console.error("Error fetching total image count: ", error);
      }
    };
    getTotalImages();
  }, []);
  
  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      try {
        let q;
        const imagesCollectionRef = collection(db, "constructionImages");

        if (pageDirection === 'initial' || currentPage === 1) {
          q = query(imagesCollectionRef, orderBy("uploadedAt", "desc"), limit(itemsPerPage));
        } else if (pageDirection === 'next' && lastVisibleDoc) {
          q = query(imagesCollectionRef, orderBy("uploadedAt", "desc"), startAfter(lastVisibleDoc), limit(itemsPerPage));
        } else if (pageDirection === 'prev' && firstVisibleDoc) {
          q = query(imagesCollectionRef, orderBy("uploadedAt", "desc"), endBefore(firstVisibleDoc), limitToLast(itemsPerPage));
        } else {
          q = query(imagesCollectionRef, orderBy("uploadedAt", "desc"), limit(itemsPerPage));
        }

        const documentSnapshots = await getDocs(q);
        let newImages = documentSnapshots.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
        
        setConstructionPhotos(newImages); 

        if (documentSnapshots.docs.length > 0) {
          setFirstVisibleDoc(documentSnapshots.docs[0]);
          setLastVisibleDoc(documentSnapshots.docs[documentSnapshots.docs.length - 1]);
          
          const potentialNextQuery = query(imagesCollectionRef, orderBy("uploadedAt", "desc"), startAfter(documentSnapshots.docs[documentSnapshots.docs.length - 1]), limit(1));
          const nextSnapshot = await getDocs(potentialNextQuery);
          setIsLastPage(nextSnapshot.empty);
        } else {
          if (pageDirection === 'next') setIsLastPage(true);
          if (pageDirection === 'prev' && currentPage === 1) {
             setFirstVisibleDoc(null);
             setLastVisibleDoc(null);
             setIsLastPage(totalImages <= itemsPerPage);
          }
          if (newImages.length === 0 && currentPage > 1) setIsLastPage(true);
          else if (newImages.length < itemsPerPage) setIsLastPage(true);
        }
      } catch (error) {
        console.error("Error fetching images for gallery: ", error);
      }
      setLoading(false);
      setPageDirection('idle');
    };
    
    if (totalImages > 0 || (pageDirection === 'initial' && currentPage === 1)) {
        fetchImages();
    } else if (totalImages === 0) { 
        setConstructionPhotos([]);
        setLoading(false);
        setIsLastPage(true); 
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, totalImages]); 
  
  const totalPages = Math.ceil(totalImages / itemsPerPage);

  const handleNextPage = () => {
    if (!isLastPage && currentPage < totalPages && !loading) {
        setPageDirection('next');
        setCurrentPage(prev => prev + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1 && !loading) {
      setPageDirection('prev');
      setCurrentPage(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  return (
    <>
      <title>시공 갤러리 - 진양건설</title>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <AnimatedSection animationType="fade-in">
          <header className="text-center pt-8 pb-12 rounded-lg mb-12 md:mb-16">
            <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 dark:text-white tracking-tight">시공 갤러리</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mt-4 max-w-2xl mx-auto">진양건설의 다양한 시공 현장 사진들을 만나보세요.</p>
          </header>
        </AnimatedSection>
        
        {loading && constructionPhotos.length === 0 && pageDirection === 'initial' && currentPage === 1 && (
             <div className="min-h-[60vh] flex justify-center items-center">
                <p className="text-xl text-gray-500 dark:text-gray-400">갤러리를 불러오는 중입니다...</p>
             </div>
        )}

        {!loading && constructionPhotos.length === 0 && totalImages === 0 && (
            <AnimatedSection animationType="fade-in-up" className="min-h-[60vh] flex justify-center items-center">
                <p className="text-xl text-gray-500 dark:text-gray-400">업로드된 시공 사진이 없습니다.</p>
            </AnimatedSection>
        )}

        {!loading && constructionPhotos.length > 0 && (
          <AnimatedSection animationType="fade-in-up" className="min-h-[60vh]">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {constructionPhotos.map((photo) => (
                <Link 
                  to={`/projects/gallery/${photo.id}`} 
                  state={{ photoItem: photo, allPhotos: constructionPhotos }} 
                  key={photo.id} 
                  className="block bg-white dark:bg-slate-800 rounded-xl shadow-xl overflow-hidden group transition-all duration-300 hover:shadow-2xl hover:-translate-y-1.5"
                >
                  <div className="relative aspect-[4/3] w-full overflow-hidden">
                    <img 
                      src={photo.imageUrl} 
                      alt={photo.title}
                      title="" 
                      draggable="false" // 드래그 방지
                      className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110 select-none" // 선택 방지
                      style={{ WebkitUserDrag: 'none' }} // Webkit 드래그 방지
                      onError={(e) => e.target.src = 'https://placehold.co/600x450/334155/e0f2fe?text=Image+Load+Error'}
                      onContextMenu={(e) => e.preventDefault()} // 오른쪽 클릭 방지
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      <h3 className="text-white text-lg font-semibold truncate">{photo.title}</h3>
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400 mb-2">
                      <span>{photo.category}</span>
                      <span>{photo.uploadedAt?.toDate ? photo.uploadedAt.toDate().toLocaleDateString() : '날짜 없음'}</span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-2 h-10">{photo.description || '상세 설명 없음'}</p>
                  </div>
                </Link>
              ))}
            </div>
          </AnimatedSection>
        )}

        {totalPages > 1 && (
          <AnimatedSection animationType="fade-in" className="mt-12 md:mt-16 flex justify-center items-center space-x-2">
            <button onClick={handlePrevPage} disabled={currentPage === 1 || loading} className={`p-2.5 rounded-lg transition-colors ${currentPage === 1 || loading ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed' : 'bg-white dark:bg-slate-700 hover:bg-sky-100 dark:hover:bg-sky-700 text-sky-600 dark:text-sky-400 shadow-md'}`} aria-label="이전 페이지">
              <ChevronLeftIcon /> 이전
            </button>
            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm font-medium text-gray-700 dark:text-gray-300">
              페이지 {currentPage} / {totalPages > 0 ? totalPages : 1}
            </span>
            <button onClick={handleNextPage} disabled={isLastPage || currentPage === totalPages || loading} className={`p-2.5 rounded-lg transition-colors ${isLastPage || currentPage === totalPages || loading ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed' : 'bg-white dark:bg-slate-700 hover:bg-sky-100 dark:hover:bg-sky-700 text-sky-600 dark:text-sky-400 shadow-md'}`} aria-label="다음 페이지">
              다음 <ChevronRightIcon />
            </button>
          </AnimatedSection>
        )}
      </div>
    </>
  );
}
export default ProjectsPage;
